import * as layouts from "layouts"
import * as api from "brandguard/api"
import * as bglayouts from "brandguard/components/layouts"
import * as scores from "brandguard/components/layouts/scores"
import { useSettings } from "accounts/settings"
import { State } from "accounts/api"
import { ReferenceChecks, ToneNVoice, Racism, Profanity, Grammar, ArtifactDetection, ProductDetection } from "brandguard/display/scores"

interface props extends layouts.containers.ContainerProps {
	item: api.TextSearchResponseItem
	prediction(evt: api.TextSearchResponseItem): api.Prediction
}

export default function Scores(props: props): JSX.Element {
	const { item, prediction, className, mx, ...rest } = props
	const parentRest = { mx }
	const settings = useSettings()

	const onBrand = {
		title: "Brand Principles",
		indev: false,
		subtitle: "Detailed view of the Brand Principles",
		subscores: [
			{
				title: "Tone of Voice",
				score: item.event?.text_tone_and_voice,
				inverse: false,
				indev: false,
				available: settings.text_tone_and_voice,
				additions: <ToneNVoice event={item.media!} />,
			},
			{
				title: "Reference Checks",
				score: item.event?.text_reference_rules,
				inverse: false,
				indev: false,
				available: settings.text_reference_rules,
				additions: <ReferenceChecks event={item.media!} />,
			},
			{
				title: "Brand Consistency",
				score: item.event?.artifact_score,
				inverse: false,
				indev: false,
				available: settings.brand_consistency,
				additions: <ArtifactDetection event={item.event!} />,
			},
		] as scores.subScoreStruct[],
	} as scores.scoreStruct

	const safety = {
		title: "Brand Safety",
		indev: false,
		subtitle: "Detailed view of the Brand Safety score",
		subscores: [
			{
				title: "Grammar",
				score: item.event?.grammar,
				inverse: false,
				indev: false,
				available: settings.grammar,
				additions: <Grammar event={item.media!} />,
			},
			{
				title: "Profanity Check",
				score: item.event?.profanity,
				inverse: false,
				indev: false,
				available: settings.profanity,
				additions: <Profanity event={item.media!} />,
			},
			{
				title: "Racism Check",
				score: item.event?.racism,
				inverse: true,
				indev: false,
				available: settings.racism,
				additions: <Racism event={item.media!} />,
			},
		] as scores.subScoreStruct[],
	}

	function NonNegotiableAlert(props: layouts.containers.FlexProps): JSX.Element {
		const { ...rest } = props
		const _onbrand = onBrand.subscores.map((i) => scores.isNonNegotiable(i.score, i.indev, i.inverse, i.available))
		const _onstyle = [scores.isNonNegotiable(item.event?.text_tone_and_voice!, false, false, settings.copy_compliance)]
		const _safety = safety.subscores.map((i) => scores.isNonNegotiable(i.score, i.indev, i.inverse, i.available))
		const _compliance = [scores.isNonNegotiable(0, true, false, settings.copy_compliance)]
		if (!_onbrand.concat(_onstyle, _safety, _compliance).includes(true)) return <></>
		return <scores.NonNegotiableMessage {...rest} />
	}

	//If there is only one category enabled => expand that category by default
	const initOpen = (): boolean => {
		const _onbrand = scores.sum(onBrand.subscores.map((i) => i.available)) === 0 ? 0 : 1
		const _safety = scores.sum(safety.subscores.map((i) => i.available)) === 0 ? 0 : 1
		return _onbrand + _safety === 1
	}

	return (
		<bglayouts.fileDetails.InformationGroup className={`${className} file-details-information-group`} {...rest}>
			{settings.confidence !== State.DISABLED && (
				<scores.BGScore
					confidence={item.event!.confidence}
					className={`${className} brandguard-score`}
				/>
			)}
			<NonNegotiableAlert height="30px" borderRadius="5px" alignItems="center" mb="15px" {...parentRest} />
			<scores.ScoreItem i={onBrand} className={className} initOpen={initOpen()} />
			<scores.ScoreItem i={safety} className={className} initOpen={initOpen()} />
			{settings.copy_compliance !== State.DISABLED && (
				<scores.NoSubscoresItem className={className} score={0} indev state={settings.copy_compliance}>
					Compliance
				</scores.NoSubscoresItem>
			)}
			{settings.text_product_detection !== State.DISABLED && (
				<ProductDetection event={item.media!} contentType="text">
					<layouts.Span fontWeight="500">Detected Products</layouts.Span>
				</ProductDetection>
			)}
		</bglayouts.fileDetails.InformationGroup>
	)
}

Scores.defaultProps = {
	prediction: (i: api.TextSearchResponseItem) => i.event?.approved || api.Prediction.NONE,
}
